import { ApplicationRef, Injectable, inject, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { NGXLogger } from 'ngx-logger';
import { concat, filter, first, interval } from 'rxjs';
import { ToastService } from '../shared/access/toast.service';

const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours
const MIN_RECHECK_ON_FOCUS_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Owns service-worker update detection. Never reloads the page unprompted while the app
 * is mid-flow — an update is queued and only surfaced (via a dismissible toast) once
 * notifyAppReady() has been called, so a background deploy can't yank a user off the
 * loading screen or out of an in-progress form.
 */
@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private appRef = inject(ApplicationRef);
  private swUpdate = inject(SwUpdate);
  private toast = inject(ToastService);
  private logger = inject(NGXLogger);

  private updateReady = false;
  private appReady = false;
  private lastCheckAt = 0;

  /**
   * Stays true for the rest of the session once a new version is downloaded, so the footer
   * keeps offering a reload after the one-off toast has been dismissed or missed.
   */
  public readonly updateAvailable = signal(false);

  public init(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    // Wait for the app to stabilize before polling — avoids competing with the initial
    // load sequence — then re-check periodically for the lifetime of the tab.
    const appIsStable$ = this.appRef.isStable.pipe(first((stable) => stable));
    const periodicCheck$ = interval(CHECK_INTERVAL_MS);
    concat(appIsStable$, periodicCheck$).subscribe(() => this.checkForUpdate());

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkForUpdate();
      }
    });

    this.swUpdate.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => {
        this.logger.log('[AppUpdate] New version ready');
        this.updateReady = true;
        this.updateAvailable.set(true);
        this.promptIfReady();
      });

    this.swUpdate.unrecoverable.subscribe((event) => {
      this.logger.error('[AppUpdate] Unrecoverable state, reloading', event);
      // The current client is broken beyond repair (e.g. asset hash mismatch) — a fresh
      // load is the only fix. Guard against a reload loop if the deployment itself is bad.
      if (sessionStorage.getItem('of_unrecoverable_reload') === '1') {
        return;
      }
      sessionStorage.setItem('of_unrecoverable_reload', '1');
      window.location.reload();
    });
  }

  /** Call once the app has finished its initial load and the real UI is showing. */
  public notifyAppReady(): void {
    this.appReady = true;
    this.promptIfReady();
  }

  private checkForUpdate(): void {
    if (Date.now() - this.lastCheckAt < MIN_RECHECK_ON_FOCUS_MS) {
      return;
    }
    this.lastCheckAt = Date.now();
    this.swUpdate.checkForUpdate().catch((err) => this.logger.warn('[AppUpdate] Update check failed', err));
  }

  private promptIfReady(): void {
    if (!this.updateReady || !this.appReady) {
      return;
    }
    this.updateReady = false;

    this.toast.open('A new version of Order Flow is available.', 'Refresh', {
      duration: 0,
      onAction: () => window.location.reload()
    });
  }
}
